import { sendEvent, track, TrackMethod } from '../Track';
import { TActionType, TGameArea, TransactionType } from '../DataSet/Transaction';
import { ResourceTrackingEventName, TransactionTrackingEventName } from '../TrackingEvent';
import { FBTrackingEvent, getIAPEventName } from '../FBTrackingEvent';
import Game from '../../../Game';
import { Random } from '../../Random';
import { IRewardData, RewardType } from '../../RewardHelper';
import { trackReward } from './Resource';
import { PurchaseFailReason } from '../../SDK/Drivers/PurchaseFailReason';
import { GameScene } from '../../../State/Game/GameScene';
import { IGameState } from '../../../State/Game/IGameState';
import { AsyncActionStatus } from '../../../State/AsyncAction';
import { IAPProduct } from '../../SDK/Drivers/IAPCatalog';
import { PurchaseStatus } from './PurchaseStatus';
import { unexpectedValue } from '../../Types';
import { report } from '../../../report';
import { ProductId } from 'Utility/Product';
import { PurchaseConsumeStatus } from '@sgorg/mobile-dev-tools';
import { isNative } from 'Utility/Platform';

export function trackResource(
    state: IGameState,
    data?: {
        transactionType?: TransactionType;
        tActionType?: TActionType;
        transactionID?: string;
    }
) {
    try {
        if (data) {
            if (data.transactionType) track('transactionType', data.transactionType);
            if (data.transactionID) track('transactionID', data.transactionID);
            if (data.tActionType) track('tActionType', data.tActionType);
        } else {
            track('transactionID', Random.ID());
        }
        track('tGameArea', getGameArea(state));
        track('tLevelContext', state.levelState.levelNumber);
        track('attributionCampaignID', state.playerState.attributionCampaignID);
    } catch (error) {
        report('Failed to track resource', {
            error,
            once: true,
            sampleRate: 0.1,
        });
    }
}

export function trackResourceEvent(
    state: IGameState,
    data?: {
        transactionType?: TransactionType;
        tActionType?: TActionType;
        transactionID?: string;
    }
) {
    try {
        data = data || {};
        if (state.activeScene.result === GameScene.LEVEL && data.transactionType !== TransactionType.LEVEL_END) return;

        trackResource(state, data);

        sendEvent(ResourceTrackingEventName.resource, state);
    } catch (error) {
        report('Failed to track resource event', {
            error,
            once: true,
            sampleRate: 0.1,
        });
    }
}

/**
 * Tracking for Transaction Event
 */
export function trackPurchase(
    state: IGameState,
    productID: string,
    rewards: IRewardData[],
    tActionType: TActionType = TActionType.INVENTORY_PURCHASE,
) {
    try {
        if(isNative()) {
            track('tActionType', TActionType.INVENTORY_PURCHASE);
        } else {
            track('tActionType', tActionType);
        }
        track('transactionType', TransactionType.PURCHASE);
        track('transactionID', Random.ID());
        track('attributionCampaignID', state.playerState.attributionCampaignID);

        const catalog: IAPProduct[] = Game.sdk.getCatalog().products;

        const product: IAPProduct = catalog.find((productData: IAPProduct) => productData.productId === productID);

        if(isNative()) {
            nativePurchaseTracking(product, tActionType);
        } else if (product && product.price) {
            const moneyAmount: number = parseInt(product.price.match(/\d/g).join(''), 10);
            track('productsSpent', {
                realCurrency: {
                    realCurrencyAmount: moneyAmount,
                    realCurrencyType: product.priceCurrencyCode,
                },
            });
        } else {
            track('productsSpent', {
                realCurrency: {
                    realCurrencyAmount: 0,
                    realCurrencyType: 'USD',
                },
            });
        }

        track('productsReceived', {});

        if (rewards && rewards.length > 0) {
            rewards.forEach((reward: IRewardData) => {
                trackReward(reward);
            });
            const coinReward: number = rewards
                .map((reward: IRewardData) => (reward.type === RewardType.COINS ? reward.value : 0))
                .reduce((previous, current) => previous + current, 0);
            track('tIAPCoins', coinReward);
            if (state.activeScene.result !== GameScene.LEVEL) {
                sendEvent(ResourceTrackingEventName.resource, state);
            }
        }
        sendEvent(TransactionTrackingEventName.transaction, state);
    } catch (e) {
        console.warn('trackPurchase failed', e);
    }
}

export function trackPurchaseStatus(productId: ProductId, status: PurchaseStatus, failReason?: PurchaseFailReason) {
    try {
        track('tProductID', productId);
        track('tPurchaseFailReason', failReason);
        track('tPurchaseStatus', status);

        const eventName = getIAPEventName(productId, status);
        if (eventName) {
            // WARNING: assuming this conversion of a custom string to FBTrackingEvent is fine
            Game.sdk.gameTracking(eventName as FBTrackingEvent);
        }
    } catch (e) {
        console.warn('trackPurchaseStatus failed', e);
    }
}

/**
 * Get the current 'game area' (according to DDNA) from the game state.
 * @param state
 */
function getGameArea(state: IGameState) {
    if (state.activeScene && state.activeScene.status !== AsyncActionStatus.EMPTY) {
        switch (state.activeScene.result) {
            case GameScene.LEVEL:
                return TGameArea.LEVEL;
            case GameScene.WORLD:
                return TGameArea.MAP;
            case GameScene.LUCKY_TURNIPS:
                return TGameArea.LUCKY_TURNIPS;
            case GameScene.MY_PET_MINI_GAME:
                return TGameArea.MY_PET_MINI_GAME;
            case GameScene.FARM:
                return TGameArea.FARM;
            default:
                unexpectedValue(state.activeScene.result, false);
        }
    }

    return TGameArea.MAP;
}

function nativePurchaseTracking(product: IAPProduct, tActionType: TActionType) {
    // @ts-ignore: Native driver passes the purchase consume status not the action type.
    const status:PurchaseConsumeStatus = tActionType;
    // Only track real currency for successfully validated IAPs
    if(product && product.price && status === PurchaseConsumeStatus.Success) {
        track('productsSpent', {
            realCurrency: {
                realCurrencyAmount: parseInt(product.price.match(/\d/g).join(''), 10),
                realCurrencyType: product.priceCurrencyCode,
            },
        });
    } else {
        track('productsSpent', {
            realCurrency: {
                realCurrencyAmount: 0,
                realCurrencyType: 'USD',
            },
        });
    }
}
